/*
  nn: Etui model — dwa kaskadowe dropdowny (marka → model) na PDP etui.

  <nn-etui-model> czyta listę modeli z metafielda sklepu (wstrzykniętą jako JSON),
  buduje dropdown marek, a po wyborze marki wypełnia drugi dropdown jej modelami
  (do tego momentu "Model" jest disabled).

  Dane do koszyka — BEZ przechwytywania wysyłki: do każdego formularza /cart/add
  w tym PDP wstawiamy raz ukryte pola properties[Model] + properties[_pelna_nazwa],
  a przy wyborze modelu tylko WPISUJEMY do nich wartość. Shopify wysyła je natywnie.
       properties[Model]        = model (widoczna, dla klienta)
       properties[_pelna_nazwa] = pełna nazwa z pliku (ukryta) → BaseLinker/produkcja
         zamawia u dostawcy po tej dokładnej nazwie

  Blokada "nie kupisz bez modelu" — lekki, LOKALNY listener submit na formularzach
  tego PDP (nie globalny capture): gdy brak modelu → preventDefault + komunikat.
  Cart/checkout nietknięte.
*/
if (!customElements.get('nn-etui-model')) {
  customElements.define(
    'nn-etui-model',
    class NnEtuiModel extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;

        this.propertyName = this.dataset.propertyName || 'Model';

        this.brandSelect = this.querySelector('[data-nn-etui-brand]');
        this.modelSelect = this.querySelector('[data-nn-etui-model]');
        this.errorEl = this.querySelector('[data-nn-etui-error]');

        const models = this.readModels();
        if (!this.brandSelect || !this.modelSelect || !models.length) return;

        this.byBrand = this.groupByBrand(models);
        this.buildBrandOptions();
        this.modelPlaceholder = this.modelSelect.querySelector('[data-placeholder]')?.textContent || '';

        // Formularze /cart/add tego PDP (główny + sticky). Wstaw pola property raz.
        this.forms = this.findForms();
        this.propEls = this.forms.map((form) => ({
          model: this.ensureProp(form, `properties[${this.propertyName}]`),
          raw: this.ensureProp(form, 'properties[_pelna_nazwa]'),
          form,
        }));

        this.chosen = null;
        this.brandSelect.addEventListener('change', () => this.onBrandChange());
        this.modelSelect.addEventListener('change', () => this.onModelChange());

        // Blokada do czasu wyboru: wyszarzenie przycisku + twardy listener na formularzach.
        this.lockSubmit(true);
        this.forms.forEach((form) => form.addEventListener('submit', (e) => this.onSubmit(e)));
      }

      readModels() {
        const tpl = this.querySelector('[data-nn-etui-models]');
        if (!tpl) return [];
        try {
          const data = JSON.parse(tpl.textContent.trim());
          return Array.isArray(data) ? data.filter((m) => m && m.model && m.brand) : [];
        } catch (e) {
          return [];
        }
      }

      groupByBrand(models) {
        const groups = {};
        for (const m of models) {
          const brand = String(m.brand).trim();
          (groups[brand] = groups[brand] || []).push({
            model: String(m.model).trim(),
            raw: m.raw != null ? String(m.raw).trim() : '',
          });
        }
        for (const b of Object.keys(groups)) {
          groups[b].sort((a, b) => a.model.localeCompare(b.model, 'pl'));
        }
        return groups;
      }

      buildBrandOptions() {
        const frag = document.createDocumentFragment();
        for (const brand of Object.keys(this.byBrand).sort((a, b) => a.localeCompare(b, 'pl'))) {
          const opt = document.createElement('option');
          opt.value = brand;
          opt.textContent = brand;
          frag.appendChild(opt);
        }
        this.brandSelect.appendChild(frag);
      }

      // Znajdź formularze /cart/add w obrębie tego PDP (główny w product-info + sticky cart).
      findForms() {
        const root = this.closest('product-info');
        const scope = root ? root.parentElement || document : document;
        return [...scope.querySelectorAll('form[action*="/cart/add"]')].filter((f) => {
          // główny PDP zawsze; sticky cart powiązany z tą samą sekcją
          return !root || root.contains(f) || f.closest('[class*="sticky-cart"]');
        });
      }

      ensureProp(form, name) {
        let el = form.querySelector(`input[name="${CSS.escape(name)}"][data-nn-etui]`);
        if (!el) {
          el = document.createElement('input');
          el.type = 'hidden';
          el.name = name;
          el.setAttribute('data-nn-etui', '');
          form.appendChild(el);
        }
        return el;
      }

      onBrandChange() {
        const brand = this.brandSelect.value;
        this.chosen = null;
        this.lockSubmit(true);
        this.writeProps();
        this.resetModelSelect();

        if (!brand || !this.byBrand[brand]) {
          this.modelSelect.disabled = true;
          return;
        }
        const frag = document.createDocumentFragment();
        for (const m of this.byBrand[brand]) {
          const opt = document.createElement('option');
          opt.value = m.model; // wartość opcji = nazwa modelu (kod usunięty z danych)
          opt.textContent = m.model;
          opt.dataset.model = m.model;
          opt.dataset.brand = brand;
          opt.dataset.raw = m.raw;
          frag.appendChild(opt);
        }
        this.modelSelect.appendChild(frag);
        this.modelSelect.disabled = false;
      }

      resetModelSelect() {
        this.modelSelect.innerHTML = '';
        const ph = document.createElement('option');
        ph.value = '';
        ph.textContent = this.modelPlaceholder;
        ph.setAttribute('data-placeholder', '');
        ph.selected = true;
        this.modelSelect.appendChild(ph);
      }

      onModelChange() {
        const opt = this.modelSelect.selectedOptions[0];
        if (!opt || !opt.value || opt.hasAttribute('data-placeholder')) {
          this.chosen = null;
          this.lockSubmit(true);
          this.writeProps();
          return;
        }
        this.chosen = {
          label: this.fullLabel(opt.dataset.brand, opt.dataset.model),
          raw: opt.dataset.raw || '',
        };
        this.writeProps();
        this.hideError();
        this.lockSubmit(false);
      }

      // Wpisz wybór do ukrytych pól property (lub wyczyść, gdy brak wyboru).
      writeProps() {
        for (const p of this.propEls) {
          p.model.value = this.chosen ? this.chosen.label : '';
          p.raw.value = this.chosen ? this.chosen.raw : '';
        }
      }

      fullLabel(brand, model) {
        brand = (brand || '').trim();
        model = (model || '').trim();
        if (!brand) return model;
        if (model.toLowerCase().startsWith(brand.toLowerCase())) return model;
        if (brand === 'Apple' && /iphone/i.test(model)) return model;
        return `${brand} ${model}`;
      }

      lockSubmit(lock) {
        const root = this.closest('product-info') || document;
        root.querySelectorAll('.product-form__submit').forEach((btn) => {
          btn.toggleAttribute('data-nn-etui-locked', lock);
        });
      }

      showError() {
        if (this.errorEl) this.errorEl.hidden = false;
      }
      hideError() {
        if (this.errorEl) this.errorEl.hidden = true;
      }

      // Twarda blokada: brak modelu → nie wpuszczamy do /cart/add.
      onSubmit(e) {
        if (this.chosen) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.showError();
        (this.brandSelect.value ? this.modelSelect : this.brandSelect).focus();
      }
    }
  );
}
